'use client'
import React, { useEffect, useState } from 'react'
import Card from '../ui/card'
import { IconBuildingLibrarySvg } from '~/icons/ic_building_library'
import { IconCreditCardSvg } from '~/icons/ic_credit_card'
import { IconMasterCardSvg } from '~/icons/ic_master_card'
import { IconVisaSvg } from '~/icons/ic_visa'
import { IconQrCodeSvg } from '~/icons/ic_qr_code'
import Message, { type MessageType } from '../ui/message'
import QRCode from 'qrcode'
import { useHotelByHotelId } from '~/hooks/use-hotels'
import { useCreditCardPayment, useQRCode } from '~/hooks/use-payment'
import { useEPayRedirect } from '~/hooks/use-epay-redirect'
import { PAYMENT_METHOD } from '~/data/language'
import CollapseItem from './collapse-item'
import { calculateDurationOfStay } from '~/lib/date-utils'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { BankTransferInfo } from './bank-transfer-info'
import { PaymentDeadlineMobile } from './payment-deadline-mobile'
import PaymentMethodOption from './payment-method-option'
import BookingSummary from './booking-summary'
import QRCodeDisplay from './qr-code-display'
import PaymentDeadlinePc from './payment-deadline-pc'
import { useSearchParams } from 'next/navigation'
import TimeoutModal from './timeout-modal'
import { useBookingCart } from '~/stores/booking-cart'
import { signature } from '~/utils/const'
import { useDevice } from '~/hooks/use-device'
import TimeoutDeadline from '~/components/payment/timeout-deadline'
import { usePaymentData } from '~/hooks/use-payment-data'
import OverlayLoading from '../ui/overlay-loading'

interface Props {
  nextStep: (dataSocket: any) => void
  hotelId: string
  dataPayment?: any
  timeIn: string
  timeOut: string
  isTimeOut: (deadline: boolean) => void
  isOpenModelStep3?: boolean
}
const WEB_SOCKET = process.env.NEXT_PUBLIC_CRS_URL
const IS_TEST_MODE = process.env.NEXT_PUBLIC_SITE_URL === 'https://test-booking.lotussolution.cloud'
type PaymentType = PAYMENT_METHOD.QR | PAYMENT_METHOD.BANK | PAYMENT_METHOD.CREDIT | ''

export default function Step2({
  nextStep,
  // dataPayment,
  hotelId,
  timeIn,
  timeOut,
  isTimeOut,
  isOpenModelStep3 = false,
}: Props) {
  const { items } = useBookingCart()
  const [paymentType, setPaymentType] = useState<PaymentType>(PAYMENT_METHOD.QR)
  const [qrCodeBase64, setQrCodeBase64] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<MessageType>('error')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(false)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const isConfirm = new URLSearchParams(window.location.search).get('confirm') || false
  const { hotel } = useHotelByHotelId(hotelId)
  const { paymentData } = usePaymentData()
  const dataPayment = paymentData as any
  const { generateQRCode, qrCode } = useQRCode(dataPayment?.bookingNo)
  const { createCreditCardPayment } = useCreditCardPayment(
    items?.[0]?.customerCode || signature.customer_code
  )
  const { redirectToEPay } = useEPayRedirect()
  const searchParams = useSearchParams()
  const epayResult = searchParams.get('epayResult')
  const totalAmount = searchParams.get('totalAmount')
  const fullName = searchParams.get('customerName')
  const bookingNo = searchParams.get('bookingNo')
  const arrivalDate = searchParams.get('checkInDate')
  const departureDate = searchParams.get('checkOutDate')
  const timeInParams = searchParams.get('timeIn') || '14:00'
  const timeOutParams = searchParams.get('timeOut') || '12:00'
  const subAmount = searchParams.get('subAmount') || 0
  const discountAmount = searchParams.get('discountAmount')
  const nights =
    arrivalDate && departureDate ? calculateDurationOfStay(arrivalDate, departureDate) : 0
  const roomCount = searchParams.get('roomCount')
  const [totalSeconds, setTotalSeconds] = useState<number>(() => {
    const stored = localStorage.getItem('PaymentDeadline')
    if (stored) {
      const cancelTimeStamp = parseInt(stored)
      const now = new Date().getTime()
      const diffInSeconds = Math.floor((cancelTimeStamp - now) / 1000)
      console.log('aaa', Math.max(0, diffInSeconds))
      return Math.max(0, diffInSeconds)
    }
    if (dataPayment?.cancelTime) {
      const now = new Date().getTime()
      const diffInSeconds = Math.floor((dataPayment.cancelTime - now) / 1000)
      localStorage.setItem('PaymentDeadline', dataPayment.cancelTime.toString())
      return Math.max(0, diffInSeconds)
    }
    const defaultCancelTime = new Date().getTime() + 15 * 60 * 1000
    localStorage.setItem('PaymentDeadline', defaultCancelTime.toString())
    return 15 * 60
  })
  const { isMobile } = useDevice()
  useEffect(() => {
    if (totalSeconds <= 0) {
      clearMinutesLeft()
      return
    }

    const interval = setInterval(() => {
      const stored = localStorage.getItem('PaymentDeadline')
      if (stored) {
        const cancelTimeStamp = parseInt(stored)
        const now = new Date().getTime()
        const diffInSeconds = Math.floor((cancelTimeStamp - now) / 1000)
        setTotalSeconds(Math.max(0, diffInSeconds))
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [totalSeconds])

  useEffect(() => {
    setPaymentType(PAYMENT_METHOD.QR)
    if (dataPayment) {
      dataPayment['nights'] = calculateDurationOfStay(
        dataPayment?.arrivalDate,
        dataPayment?.departureDate
      )
    }
  }, [])

  useEffect(() => {
    if (paymentData && paymentData.email) {
      const url = new URL(window.location.href)
      url.searchParams.set('step', '2')
      url.searchParams.set('epayResult', 'true')
      url.searchParams.set('bookingNo', `${paymentData?.bookingNo}`)
      url.searchParams.set('customerName', `${paymentData?.customerName}`)
      url.searchParams.set('totalAmount', `${paymentData?.totalAmount}`)
      url.searchParams.set('roomCount', `${paymentData?.totalRooms}`)
      url.searchParams.set('timeIn', `${paymentData?.timeIn}`)
      url.searchParams.set('timeOut', `${paymentData?.timeOut}`)
      url.searchParams.set('subAmount', `${paymentData?.subAmount}`)
      if (paymentData?.discountAmount) {
        url.searchParams.set('discountAmount', `${paymentData?.discountAmount}`)
      }
      const newUrl = url.toString()
      window.history.replaceState({}, '', `${newUrl}`)
    }
  }, [paymentData])

  const clearMinutesLeft = () => {
    localStorage.removeItem('PaymentDeadline')
  }

  useEffect(() => {
    if (paymentType) {
      handlePaymentTypeChange()
    }
  }, [paymentType, dataPayment, bookingNo])

  const handlePaymentTypeChange = async () => {
    try {
      switch (paymentType) {
        case PAYMENT_METHOD.QR:
          setLoadingMessage(t('payment.creatingQr'))
          if (epayResult != 'epay') {
            try {
              await handleQRCodeGeneration()
            } catch (error) {
              console.error('Failed to generate QR code:', error)
              setMessage(t('payment.qrError'))
              setMessageType('error')
            }
          }
          break
        case PAYMENT_METHOD.CREDIT:
          setLoadingMessage(t('payment.connectingGateway'))
          try {
            await handleCreditCardPayment()
          } catch (error) {
            console.error('Failed to process credit card payment:', error)
            setMessage(t('payment.creditError'))
            setMessageType('error')
          }
          setPaymentType('')
          break
      }
    } catch (error) {
      console.error('Error handling payment type:', error)
      setMessage(t('payment.processError'))
      setMessageType('error')
    } finally {
      setLoadingMessage('')
    }
  }

  const handleQRCodeGeneration = async () => {
    if (!dataPayment && !bookingNo) return
    setIsLoading(true)
    const dataRequest = {
      bookingNo: dataPayment?.bookingNo || bookingNo,
      customerName: dataPayment?.fullName || fullName,
      note: '',
      voucherCode: '',
    }
    try {
      if (qrCode?.vietQR) {
        const qrImage = await QRCode.toDataURL(qrCode.vietQR)
        setQrCodeBase64(qrImage)
        setIsLoading(false)
      } else {
        generateQRCode(dataRequest, {
          onSuccess: async (response) => {
            if (response.isSuccess && response.data?.vietQR) {
              const qrImage = await QRCode.toDataURL(response.data.vietQR)
              setQrCodeBase64(qrImage)
              setTimeout(() => {
                setIsLoading(false)
              }, 200)
            } else {
              setMessage(response.errors?.[0]?.message || t('payment.qrError'))
              setMessageType('error')
              setIsLoading(false)
            }
          },
          onError: (error) => {
            console.error('Error generating QR code:', error)
            setMessage(t('payment.qrError'))
            setMessageType('error')
            setIsLoading(false)
          },
        })
      }
    } catch (error) {
      console.error('Error in QR code generation:', error)
      setMessage(t('payment.qrError'))
      setMessageType('error')
    }
  }

  const constructBookingUrl = () => {
    const siteUrl = window.location.origin
    const path = window.location.pathname
    const params = new URLSearchParams()
    params.set('hotelId', hotelId)
    params.set('checkInDate', dataPayment?.arrivalDate || '')
    params.set('checkOutDate', dataPayment?.departureDate || '')
    params.set('adults', String(dataPayment?.cardItems?.[0]?.guests?.adults || 1))
    params.set('kidsOver6', String(dataPayment?.cardItems?.[0]?.guests?.children6To12 || 0))
    params.set('kidsUnder6', String(dataPayment?.cardItems?.[0]?.guests?.childrenUnder6 || 0))
    params.set('bookingNo', dataPayment?.bookingNo || bookingNo)
    params.set('totalAmount', dataPayment?.totalAmount)
    params.set('customerName', dataPayment?.customerName)
    return `${siteUrl}${path}?${params.toString()}`
  }

  const handleCreditCardPayment = async () => {
    setIsLoading(true)
    const storedTime = localStorage.getItem('PaymentDeadline')
    let timeInMinutes = 15 // default 15 minutes

    if (storedTime) {
      const cancelTimeStamp = parseInt(storedTime)
      const now = new Date().getTime()
      const diffInSeconds = Math.floor((cancelTimeStamp - now) / 1000)
      timeInMinutes = Math.max(1, Math.ceil(diffInSeconds / 60)) // At least 1 minute
    }

    const domain = constructBookingUrl()
    let detailGoods = dataPayment?.cardItems?.map((e) => ({
      // ...e,
      goodsName: e.roomName,
      goodsPrice: e.amount,
      goodsCode: e.roomCode,
      goodsQuantity: 1,
    }))

    let request = {
      orderCode: '',
      billCode: dataPayment?.bookingNo || bookingNo,
      paymentType: 1,
      messageType: 'create_order',
      channelCode: '01',
      paymentMethod: '03',
      typeCardAccount: 'Card',
      businessType: '0000',
      totalAmount: dataPayment?.totalAmount || totalAmount,
      orderAmount: dataPayment?.totalAmount || totalAmount,
      feeAmount: 0,
      orderDescription: 'Thanh toán đặt phòng',
      currency: 'VND',
      returnUrl: `${domain}&step=3`,
      cancelUrl: `${domain}&step=1&epayResult=cancel`,
      againUrl: `${domain}&step=2&epayResult=back`,
      timeLimit: timeInMinutes,
      customerFullName: dataPayment?.fullName || fullName,
      customerEmail: '',
      customerMobile: '',
      customerAddress: '',
      totalGoods: 1,
      detailGoods: detailGoods,
    }
    try {
      createCreditCardPayment(request, {
        onSuccess: (response) => {
          if (response?.message === 'Success') {
            const paymentUrl = response?.result?.paymentUrl
            if (paymentUrl) {
              redirectToEPay({
                paymentUrl,
                onError: (error) => {
                  console.error('Failed to redirect to EPAY:', error)
                  setMessage(t('payment.creditError'))
                  setMessageType('error')
                },
              })
            } else {
              setMessage(t('payment.creditError'))
              setMessageType('error')
            }
            setTimeout(() => {
              setIsLoading(false)
            }, 2000)
          } else {
            setMessage(response?.result?.errorDesc || t('payment.creditError'))
            setMessageType('error')
            setIsLoading(false)
          }
        },
        onError: (error) => {
          console.error('Error processing credit card payment:', error)
          setMessage(t('payment.creditError'))
          setMessageType('error')
          setIsLoading(false)
        },
      })
    } catch (error) {
      console.error('Error in credit card payment:', error)
      setMessage(t('payment.creditError'))
      setMessageType('error')
    }
  }

  const downloadQRCode = async () => {
    const byteString = atob(qrCodeBase64.split(',')[1])
    const mimeString = qrCodeBase64.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    const blob = new Blob([ab], { type: mimeString })
    const isMobileView = window.innerWidth < 768
    const file = new File([blob], 'QRCode.png', { type: mimeString })

    // Safari iOS can't save images to the Photos library via <a download> (only to Files),
    // so on mobile we prefer the native share sheet, which offers "Save to Photos".
    // Chromium desktop also supports navigator.share, but there it just opens the OS share
    // popup with no benefit over <a download>, so we intentionally keep desktop on <a download>.
    if (isMobileView && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] })
      } catch {
        // User dismissed the share sheet, nothing to do.
      }
      return
    }

    const blobUrl = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = 'QRCode.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(blobUrl)
  }

  return (
    <>
      <Card className="xs: !bg-transparent !p-0 sm:mb-4 sm:p-4" padding={0}>
        {isLoading && <OverlayLoading show={true} text={loadingMessage} />}
        <div className="grid w-full grid-cols-1 md:grid-cols-6 md:gap-4">
          {!isConfirm && isMobile && (
            <PaymentDeadlineMobile
              time={String(totalSeconds)}
              setIsTimeoutModalOpen={(event) => {
                if (event) {
                  isTimeOut(true)
                  setIsTimeoutModalOpen(false)
                }
              }}
            />
          )}
          <div className="bg-white p-4 sm:p-6 md:col-span-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-lora text-[16px] font-semibold text-secondary-950 sm:block md:text-[24px]">
                {t('payment.method')}
              </h2>
              {!isConfirm && !isMobile && (
                <PaymentDeadlinePc
                  time={String(totalSeconds)}
                  setIsTimeoutModalOpen={setIsTimeoutModalOpen}
                />
              )}
            </div>

            <form>
              <div className="flex flex-col gap-4 overflow-hidden">
                <PaymentMethodOption
                  icon={<IconQrCodeSvg />}
                  label={t('payment.qr')}
                  value={PAYMENT_METHOD.QR}
                  checked={paymentType === PAYMENT_METHOD.QR}
                  onChange={(value) => setPaymentType(value as PaymentType)}
                />

                <PaymentMethodOption
                  icon={<IconBuildingLibrarySvg />}
                  label={t('payment.bank')}
                  value={PAYMENT_METHOD.BANK}
                  checked={paymentType === PAYMENT_METHOD.BANK}
                  onChange={(value) => setPaymentType(value as PaymentType)}
                />

                <PaymentMethodOption
                  icon={<IconCreditCardSvg />}
                  label={t('payment.credit')}
                  value={PAYMENT_METHOD.CREDIT}
                  rightContent={
                    <>
                      <IconVisaSvg /> <IconMasterCardSvg />
                    </>
                  }
                  showArrow
                  onChange={() => setPaymentType(PAYMENT_METHOD.CREDIT)}
                />
              </div>
            </form>

            {paymentType === PAYMENT_METHOD.QR && qrCodeBase64 && (
              <QRCodeDisplay
                qrCodeBase64={qrCodeBase64}
                onDownload={downloadQRCode}
                downloadButtonText={t('payment.download')}
              />
            )}

            {paymentType === PAYMENT_METHOD.BANK && !!hotel && (
              <BankTransferInfo
                bankName={hotel.bankName || '-'}
                bankAccount={hotel.bankAccount || '-'}
                bankAccountHolder={hotel.accountHolder || '-'}
                amount={dataPayment?.totalAmount || totalAmount}
                bookingNo={dataPayment?.bookingNo || bookingNo}
                customerName={dataPayment?.customerName}
              />
            )}

            {IS_TEST_MODE && (
              <div className="border-t border-dashed border-orange-300 p-4">
                <p className="mb-2 text-center text-xs text-orange-500">🛠 Dev Only</p>
                <button
                  type="button"
                  onClick={() => {
                    const fakeSocketData = {
                      Content: {
                        Data: {
                          BookingStatus: 'SUCCESS',
                        },
                      },
                    }
                    console.log('[DEBUG] Fake booking success:', fakeSocketData)
                    nextStep(fakeSocketData)
                  }}
                  className="w-full rounded-lg border-2 border-dashed border-orange-400 bg-orange-50 py-2.5 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                >
                  🚀 Fake Booking Success
                </button>
              </div>
            )}
          </div>

          <div className="hidden bg-white sm:block md:col-span-2">
            <CollapseItem title={t('booking.yourBooking')} active>
              <BookingSummary
                arrivalDate={dataPayment?.arrivalDate || arrivalDate}
                departureDate={dataPayment?.departureDate || departureDate}
                timeIn={timeIn || timeInParams}
                timeOut={timeOut || timeOutParams}
                nights={dataPayment?.nights || nights}
                roomCount={dataPayment?.cardItems?.length || roomCount}
                discountAmount={dataPayment?.discountAmount || discountAmount}
                subAmount={dataPayment?.subAmount || subAmount}
                totalAmount={dataPayment?.totalAmount || totalAmount}
                t={t}
              />
            </CollapseItem>
          </div>
        </div>

        {message && (
          <Message message={message} status={messageType} onClose={() => setMessage('')} />
        )}
      </Card>
      {!isMobile && (
        <TimeoutModal
          isOpen={isTimeoutModalOpen && !isOpenModelStep3}
          onClose={() => setIsTimeoutModalOpen(false)}
        >
          <TimeoutDeadline hotelId={hotelId} dataPayment={dataPayment} />
        </TimeoutModal>
      )}
    </>
  )
}
